// v2 market — a covered bazaar: four stout masts carrying a grand two-tier canopy
// — a broad red pavilion roof under a smaller upper tier on a cream lantern drum,
// crowned by a gold coin emblem — open on all sides over goods stalls (counters
// of wares, bolts of cloth), a central aisle rug, hanging banners on the street
// edge and two pennant poles at the front corners. Footprint-native 3x3. Ported
// 1:1 from age-of-mcp's v2/market.ts, using the library's shared `foundationSlab`,
// `barrel`, `crate` and `village` (buildScaffold, sack) helpers.
import * as THREE from "three";
import { defineModel, foundationSlab, barrel, crate, village, type Part } from "../../lib";

const BASE   = 0.14;
const POST   = "#7a5a36";
const AWNING = "#b23a34";
const CREAM  = "#efe6cf";
const COIN   = "#e8c14a";
const MAST_H = 1.16;
const MAST_TOP = BASE + MAST_H;          // 1.30 — the canopy rests here
const WARES  = ["#e07a3c", "#7aa83c", "#9c4b8c"];

// The four canopy masts, `h` tall.
function masts(h: number): Part[] {
  const out: Part[] = [];
  for (const [x, z] of [[-0.85, -0.85], [0.85, -0.85], [-0.85, 0.85], [0.85, 0.85]] as const) {
    out.push({ geometry: new THREE.BoxGeometry(0.09, h, 0.09), color: POST,
      position: [x, BASE + h / 2, z] });
  }
  return out;
}

// A goods counter at (cx, cz) with three wares boxes on top (`offset` shifts
// the ware colours so the stalls differ).
function stall(cx: number, cz: number, offset: number): Part[] {
  const out: Part[] = [
    { geometry: new THREE.BoxGeometry(0.66, 0.30, 0.30), color: "#9a7440",
      position: [cx, 0.29, cz] },
  ];
  for (let i = 0; i < 3; i++) {
    out.push({ geometry: new THREE.BoxGeometry(0.13, 0.12, 0.15),
      color: WARES[(i + offset) % 3]!,
      position: [cx - 0.20 + i * 0.20, 0.50, cz] });
  }
  return out;
}

function stage(s: number): Part[] {
  if (s <= 0) return [foundationSlab(2.94)];
  if (s === 1) {
    return [foundationSlab(2.94), ...masts(0.6), ...village.buildScaffold(1.9, 1.9, 0.9)];
  }
  if (s === 2) {
    return [foundationSlab(2.94), ...masts(MAST_H),
      ...stall(-0.62, 0.55, 0), ...stall(0.62, 0.55, 1),
      ...village.buildScaffold(1.9, 1.9, 1.5)];
  }

  return [
    foundationSlab(2.94),
    ...masts(MAST_H),
    // Ring beams tying the mast tops together.
    { geometry: new THREE.BoxGeometry(1.86, 0.07, 0.09), color: POST, position: [0, 1.27, -0.85] },
    { geometry: new THREE.BoxGeometry(1.86, 0.07, 0.09), color: POST, position: [0, 1.27, 0.85] },
    { geometry: new THREE.BoxGeometry(0.09, 0.07, 1.86), color: POST, position: [-0.85, 1.27, 0] },
    { geometry: new THREE.BoxGeometry(0.09, 0.07, 1.86), color: POST, position: [0.85, 1.27, 0] },
    // The grand canopy: broad lower pyramid, cream lantern drum, upper
    // tier, then the gold coin emblem on its finial.
    { geometry: new THREE.ConeGeometry(1.62, 0.6, 4), color: AWNING,
      position: [0, MAST_TOP + 0.30, 0], rotation: [0, Math.PI / 4, 0] },
    { geometry: new THREE.BoxGeometry(0.56, 0.20, 0.56), color: CREAM,
      position: [0, 1.90, 0] },
    { geometry: new THREE.ConeGeometry(0.78, 0.42, 4), color: "#c94e42",
      position: [0, 2.21, 0], rotation: [0, Math.PI / 4, 0] },
    { geometry: new THREE.CylinderGeometry(0.02, 0.02, 0.18, 4), color: POST,
      position: [0, 2.47, 0] },
    { geometry: new THREE.CylinderGeometry(0.12, 0.12, 0.04, 12), color: COIN,
      position: [0, 2.52, 0], rotation: [Math.PI / 2, 0, 0] },
    // Goods stalls under the canopy: two facing the aisle, one across the
    // back carrying bolts of cloth.
    ...stall(-0.62, 0.55, 0),
    ...stall(0.62, 0.55, 1),
    { geometry: new THREE.BoxGeometry(0.66, 0.30, 0.30), color: "#9a7440",
      position: [0, 0.29, -0.75] },
    { geometry: new THREE.CylinderGeometry(0.06, 0.06, 0.4, 6), color: "#3a8a8a",
      position: [-0.12, 0.49, -0.75], rotation: [0, 0, Math.PI / 2] },
    { geometry: new THREE.CylinderGeometry(0.06, 0.06, 0.4, 6), color: "#9c4b8c",
      position: [0.14, 0.49, -0.75], rotation: [0, 0, Math.PI / 2] },
    // The central aisle rug.
    { geometry: new THREE.BoxGeometry(0.70, 0.02, 0.50), color: "#8a3a4a",
      position: [0, 0.15, 0.05] },
    // Banners hanging from the street-side eave.
    { geometry: new THREE.BoxGeometry(0.18, 0.34, 0.025), color: "#c23b54",
      position: [-0.5, 1.16, 1.10] },
    { geometry: new THREE.BoxGeometry(0.18, 0.34, 0.025), color: "#3a8a8a",
      position: [0.5, 1.16, 1.10] },
    // Pennant poles at the front corners of the plot.
    { geometry: new THREE.CylinderGeometry(0.028, 0.028, 0.95, 5), color: POST,
      position: [-1.3, 0.615, 1.3] },
    { geometry: new THREE.BoxGeometry(0.20, 0.10, 0.02), color: "#d2a32e",
      position: [-1.19, 1.02, 1.3] },
    { geometry: new THREE.CylinderGeometry(0.028, 0.028, 0.95, 5), color: POST,
      position: [1.3, 0.615, 1.3] },
    { geometry: new THREE.BoxGeometry(0.20, 0.10, 0.02), color: "#d2a32e",
      position: [1.19, 1.02, 1.3] },
    // Goods waiting about the stalls.
    ...village.sack(-0.35, 1.0, BASE),
    ...village.sack(-1.0, 0.55, BASE),
    crate(0.40, 1.0, 0.28),
    crate(0.62, 1.06, 0.22),
    barrel(1.0, 0.6),
  ];
}

export const marketV2 = defineModel({
  id: "market-v2",
  tiles: { w: 3, h: 3 },
  batchId: "fable",
  name: "Market (v2)",
  tags: ["building","market","low-poly","age-of-mcp","fable"],
  summary: "Low-poly market.",
  description: "A grand trading hall where players buy and sell goods.",
  author: "age-of-mcp",
  footprint: 1.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => stage(0) },
    { id: "stage-1", label: "Lower", parts: () => stage(1) },
    { id: "stage-2", label: "Upper", parts: () => stage(2) },
    { id: "complete", label: "Complete", parts: () => stage(3) },
  ],
  animations: [],
});
